import React, { Key } from "react";
import { Table, Button, Tooltip, Tag } from "antd";
import type {
  ColumnsType,
  TablePaginationConfig,
  TableProps,
} from "antd/es/table";
import { CopyOutlined } from "@ant-design/icons";
import type { RecordLogItem } from "../api";

/**
 * 主表格组件 Props
 */
export interface RecordLogTableProps {
  data: RecordLogItem[];
  loading: boolean;
  pagination: TablePaginationConfig;
  selectedRowKeys: React.Key[];
  onChange: TableProps<RecordLogItem>["onChange"];
  onRowSelectionChange: (selectedRowKeys: Key[]) => void;
  onShowJsonModal: (title: string, content: string) => void;
  onCopyCurl: (requestId: string) => void;
  /** 通用文本复制函数 */
  onCopyText: (
    textToCopy: string | undefined | null,
    typeLabel?: string
  ) => Promise<void>;
}

/**
 * 主表格组件
 * @param props - 组件属性
 * @returns React 元素
 */
const RecordLogTable: React.FC<RecordLogTableProps> = (props) => {
  const {
    data,
    loading,
    pagination,
    selectedRowKeys,
    onChange,
    onRowSelectionChange,
    onShowJsonModal,
    onCopyCurl,
    onCopyText,
  } = props;

  const isBlank = new URLSearchParams(window.location.search).get("taskId");

  /**
   * 表格列定义
   */
  const columns: ColumnsType<RecordLogItem> = [
    {
      title: "请求",
      dataIndex: "request_path",
      key: "request",
      render: (path: string, record: RecordLogItem) => (
        <span
          title={`${record.request_host}${path}`}
        >{`${record.request_host}${path}`}</span>
      ),
      width: 300,
    },
    {
      title: "业务线",
      dataIndex: "business",
      key: "business",
      width: 100,
      align: "center",
      render: (businessLine: string) => (
        <Tag color="blue">{businessLine || "未知"}</Tag>
      ),
    },
    {
      title: "环境",
      dataIndex: "cluster_env",
      key: "cluster_env",
      width: 100,
      align: "center",
      render: (env: string) => <Tag color="green">{env || "未知"}</Tag>,
    },
    {
      title: "状态码",
      dataIndex: "response_status",
      key: "response_status",
      width: 100,
      align: "center",
      render: (status: string) => {
        const statusNum = parseInt(status, 10);
        if (statusNum >= 200 && statusNum < 300)
          return <Tag color="success">{status}</Tag>;
        if (statusNum >= 400 && statusNum < 500)
          return <Tag color="warning">{status}</Tag>;
        if (statusNum >= 500) return <Tag color="error">{status}</Tag>;
        return <Tag>{status || "N/A"}</Tag>;
      },
    },
    {
      title: "请求时间",
      dataIndex: "timestamp",
      key: "timestamp",
      width: 180,
      align: "center",
      render: (timestamp: string) => {
        if (!timestamp) return "-";
        try {
          const formatTime = Number(timestamp) / 1000000;
          const date = new Date(formatTime);
          return date.toLocaleString("zh-CN", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hour12: false,
          });
        } catch (error) {
          return timestamp;
        }
      },
    },
    {
      title: "sid",
      dataIndex: "request_id",
      key: "sid",
      ellipsis: false,
      width: 220,
      render: (requestId: string) => (
        <div style={{ display: "flex", alignItems: "center" }}>
          <Tooltip title={requestId}>
            <span
              style={{
                flexGrow: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                marginRight: "8px",
              }}
            >
              {requestId}
            </span>
          </Tooltip>
          <Tooltip title="复制 Request ID">
            <Button
              type="text"
              size="small"
              icon={<CopyOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                onCopyText(requestId, "Request ID");
              }}
            />
          </Tooltip>
        </div>
      ),
    },
    {
      title: "请求头",
      dataIndex: "request_headers",
      key: "request_headers",
      width: 80,
      align: "center",
      render: (headers: string) =>
        headers ? (
          <Button
            size="small"
            type="link"
            onClick={() => onShowJsonModal("Request Header", headers)}
          >
            查看
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: "Body",
      dataIndex: "request_body",
      key: "body",
      width: 80,
      align: "center",
      fixed: "right",
      render: (body: string) =>
        body ? (
          <Button
            size="small"
            type="link"
            onClick={() => onShowJsonModal("Body", body)}
          >
            查看
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: "Response",
      dataIndex: "response_body",
      key: "response",
      width: 100,
      align: "center",
      fixed: "right",
      render: (resp: string) =>
        resp ? (
          <Button
            size="small"
            type="link"
            onClick={() => onShowJsonModal("Response Body", resp)}
          >
            查看
          </Button>
        ) : (
          "-"
        ),
    },
    {
      title: "cURL 复制",
      key: "action",
      width: 120,
      align: "center",
      fixed: "right",
      render: (_: unknown, record: RecordLogItem) => (
        <Tooltip title="复制 cURL 命令">
          <Button
            size="small"
            type="link"
            onClick={() => onCopyCurl(record.request_id)}
          >
            复制
          </Button>
        </Tooltip>
      ),
    },
  ];

  const BlankColumns = [
    {
      title: "meta",
      dataIndex: "meta",
      key: "meta",
    },
    {
      title: "record_request_id",
      dataIndex: "record_request_id",
    },
    {
      title: "request",
      dataIndex: "request",
      width: 700,
      render: (request: string) => {
        return <pre>{window.atob(request)}</pre>;
      },
    },
    {
      title: "request_id",
      dataIndex: "request_id",
    },
    {
      title: "response",
      dataIndex: "response",
      render: (response: string) => {
        return <pre>{window.atob(response)}</pre>;
      },
    },
  ];

  /**
   * 行选择配置
   */
  const rowSelection = {
    selectedRowKeys,
    onChange: onRowSelectionChange,
    fixed: "left" as const,
  };

  return isBlank ? (
    <Table<RecordLogItem>
      rowKey={(record) =>
        record.request_id || `${record.request_host}-${record.request_path}`
      }
      columns={BlankColumns}
      dataSource={data}
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      rowSelection={rowSelection}
      scroll={{ x: "fit-content" }}
      bordered
      size="middle"
    />
  ) : (
    <Table<RecordLogItem>
      rowKey={(record) =>
        record.request_id || `${record.request_host}-${record.request_path}`
      }
      columns={columns}
      dataSource={data}
      loading={loading}
      pagination={pagination}
      onChange={onChange}
      rowSelection={rowSelection}
      scroll={{ x: "max-content" }}
      bordered
      size="middle"
    />
  );
};

export default RecordLogTable;
